// Keeps one live connection to the server. Whatever the server pushes is written straight into
// the query cache, so only the screen pieces that use that data re-draw.
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES, type LiveEventName, type LiveEvents } from '@serb/shared';
import { KEYS } from '../api/keys';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting';

export function useLive(): LiveStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');

  useEffect(() => {
    const es = new EventSource(ROUTES.api.live);
    const on = <K extends LiveEventName>(event: K, handle?: (data: LiveEvents[K]) => void) =>
      es.addEventListener(event, (ev) => {
        setStatus('live');
        if (handle) handle(JSON.parse((ev as MessageEvent<string>).data) as LiveEvents[K]);
      });
    on('hello');
    on('tick');
    on('clock', (c) => qc.setQueryData(KEYS.clock, c));
    on('card', () => void qc.invalidateQueries({ queryKey: KEYS.cards }));
    on('task', () => void qc.invalidateQueries({ queryKey: KEYS.tasks }));
    on('brain', () => void qc.invalidateQueries({ queryKey: KEYS.brain }));
    es.onerror = () => setStatus('reconnecting');
    return () => es.close();
  }, [qc]);

  return status;
}
